import { useStoreStore } from '@/stores/storeStore';
import { SectionType } from '@/types/store';
import { useCallback } from 'react';
import { Routes } from '../components/CustomizationMenu';
import { useMenuNavigation } from '../components/Menu';
import { useCustomizationStore } from '../stores/customizationStore';
import { useThemeEditorStore } from '../stores/themeEditorStore';
import { ItemType, StoreItem } from '../types/store';
import { getItemById } from '../utils/items';

export function useBlocks() {
    const { navigateById } = useMenuNavigation();
    const { defaultStoreId } = useStoreStore();
    const { setEditBlockId } = useThemeEditorStore();

    const editBlock = useCallback(
        (id: number | string) => {
            const { storeItems, clearPendingItems } = useCustomizationStore.getState();
            clearPendingItems();

            setEditBlockId(id);
            switch (id) {
                case 'header':
                    navigateById(Routes.EDIT_BLOCK_HEADER);
                    break;
                default: {
                    const item = getItemById(id, storeItems);

                    switch (item?.type) {
                        case ItemType.PAGE:
                            navigateById(Routes.EDIT_BLOCK_PRODUCT_LAYOUT, item.id);
                            return;
                        case SectionType.EDITORIAL_TEXT:
                            navigateById(Routes.EDIT_BLOCK_EDITORIAL_TEXT, item.id);
                            return;
                        case SectionType.GALLERY:
                            navigateById(Routes.EDIT_BLOCK_GALLERY, item.id);
                            return;
                        case SectionType.TESTIMONIALS:
                            navigateById(Routes.EDIT_BLOCK_TESTIMONIALS, item.id);
                            return;
                        case SectionType.TITLE:
                            navigateById(Routes.EDIT_BLOCK_TITLE, item.id);
                            return;
                        default:
                            console.warn('Could not find block type', item);
                            break;
                    }
                }
            }
        },
        [navigateById, setEditBlockId]
    );

    const addBlock = useCallback(
        (atId: number, order: number) => {
            const { storeItems, addItem, clearPendingItems } = useCustomizationStore.getState();
            clearPendingItems();

            const block = getItemById(atId, storeItems) as StoreItem;
            const atOrder = block?.order ?? 0;
            const newOrder = atOrder + order;

            if (!defaultStoreId) {
                console.warn('No default store id found');
                return;
            }

            const item = {
                store_id: defaultStoreId,
                status: 1,
                order: newOrder,
                type: ItemType.PENDING,
            };
            const id = addItem(item);
            setEditBlockId(id);
            navigateById(Routes.ADD_BLOCK, id);
        },
        [navigateById, defaultStoreId, setEditBlockId]
    );

    const deleteBlock = useCallback(
        (atId: number) => {
            const { deleteItem } = useCustomizationStore.getState();
            const { setEditBlockId } = useThemeEditorStore.getState();
            deleteItem(atId);
            setEditBlockId(null);
        },
        [setEditBlockId]
    );

    return { editBlock, addBlock, deleteBlock };
}
